import { WebsiteLevel, WebsiteConfig, ClientWebsiteGeocoderProvider } from "../types/WebsiteConfig";
import { ServiceManager, ServiceFactory, ServiceType } from "./ServiceManager";
import { SkywalkerRUM } from "./RUM/SkywalkerRUM";
import { API } from "./API/API";
import { PROListAnalyticsAdapter, FacebookAnalyticsAdapter, GoogleAnalyticsAdapter, AnalyticsEvent } from "./Analytics";
import { MapboxGeocoder, GoogleMapsGeocoder } from "./Geocoders";
import { FirebaseConfig } from "./ThirdParty/Firebase/Config";
import { Firebase } from "./ThirdParty/Firebase";
import { BrowserStorageService } from "./BrowserStorageService";
import { GeocoderCache } from "./Geocoders/GeocoderCache";
import { ContactCorrelationAdapter } from "./CRM/ContactCorrelationAdapter";
import { AutosaveManager } from "./Autosave/AutosaveManager";
import { ReCaptchaService } from "./Antispam/ReCaptchaService";
import { Client } from "./API/Client";
import { AuthToken } from "../types/Auth";

interface ConfigRoot {
  Mode: string;
  Website: ClientWebsiteConfig;
  API: APIConfig;
  RUM: RUMConfig;
  Services: ServicesConfig;
}

interface ServicesConfig {
  Firebase?: FirebaseConfig;
}

interface APIConfig {
  Base: string;
  WebsiteId: string;
  WebsiteLevel: WebsiteLevel;
}

interface ClientWebsiteConfig {
  Id: string;
  Name: string;
  Settings?: WebsiteConfig;
}

interface SkywalkerRUMConfig {
  Base: string;
}

interface RUMConfig {
  Skywalker: SkywalkerRUMConfig;
}

class Config {
    static API: APIConfig;
    static Website: ClientWebsiteConfig;
    static RUM: RUMConfig;

    static Configure(config: ConfigRoot) : Promise<boolean> {
      this.API = config.API;
      this.Website = config.Website;
      this.RUM = config.RUM;

      //Register services not dependant on configuration from the API
      ServiceManager.Register(new BrowserStorageService());
      ServiceManager.Register(new SkywalkerRUM(this.Website));

      return new Promise((resolve, reject) => {
        API.Website.GetConfiguration(config.Website.Id).then(websiteConfig => {
          this.Website.Settings = websiteConfig;

          //Configure any required services
          this.RegisterServices(config);

          resolve(true);
        }).catch(reject);
      });
    }

    static RegisterServices(config: ConfigRoot) {
      let hasPROListAnalytics = false;

      //PROLIST Analytics
      if(this.Website.Settings?.Analytics.PROListAnalyticsWebsiteId) {
        hasPROListAnalytics = ServiceManager.TryRegister('PROLIST Analytics', () => new PROListAnalyticsAdapter(this.Website));
      }

      //Facebook Pixel(s)
      if(this.Website.Settings?.Analytics.FacebookTrackingIds.length) {
        ServiceManager.TryRegister('Facebook Analytics', () => new FacebookAnalyticsAdapter(this.Website));
      }

      //Google Analytics
      if(this.Website.Settings?.Analytics.GoogleAnalyticsTrackingIds.length) {
        ServiceManager.TryRegister('Google Analytics', () => new GoogleAnalyticsAdapter(this.Website));
      }

      //Firebase
      if(config.Services.Firebase != null) {
        ServiceManager.TryRegister('Firebase', () => new Firebase(config.Services.Firebase!));
      }

      //Contact Correlation Adapter
      if(hasPROListAnalytics) {
        ServiceManager.TryRegister('Contact Correlation Adapter', () => new ContactCorrelationAdapter());
      }

      //On-demand services
      ServiceManager.RegisterFactory(ServiceType.FormAutosave, () => new AutosaveManager(config.Website));

      //Geocoder Cache
      ServiceManager.RegisterFactory(ServiceType.GeocoderCache, () => new GeocoderCache());

      //Geocoders
      switch(this.Website.Settings?.GeocoderProvider.GeocoderProvider) {
        case ClientWebsiteGeocoderProvider.Mapbox:
          ServiceManager.RegisterFactory(ServiceType.Geocoder, () => new MapboxGeocoder(this.Website.Settings!.GeocoderProvider));
          break;
        case ClientWebsiteGeocoderProvider.GoogleMaps:
          ServiceManager.RegisterFactory(ServiceType.Geocoder, () => new GoogleMapsGeocoder(this.Website.Settings!.GeocoderProvider));
          break;
      }

      //Recaptcha
      ServiceManager.Register(new ReCaptchaService());

      //Check to see if there are any credentials which we may need to send
      const bss = ServiceManager.Get<BrowserStorageService>(ServiceType.BrowserStorage);

      if (bss !== null) {
        const token = bss.getItem('prolist.api-credentials');

        if (token) {
          Client.AuthToken = JSON.parse(token) as AuthToken;
        }
      }
    }
}

export { Config, ConfigRoot, ClientWebsiteConfig }
